import accessorProjection from "../../cogo-validator/ontology/expression-accessor-globals.v1.json" with { type: "json" };

export const ACTIONFLOW_TRACE_CONTRACT = {
  version: "actionflow-trace.v1",
  triggers: ["event", "lifecycle", "init", "manual", "tick"],
  statuses: [
    "ok",
    "stopped",
    "error",
    "cancelled",
    "timed-out",
    "deduplicated",
  ],
  stepStatuses: ["ok", "skipped", "error"],
  traceIdPattern:
    /^actionflow:[a-z0-9:_-]+:(event|lifecycle|init|manual|tick)$/,
} as const;

export const ACTIONFLOW_EXECUTION_CONTROL_CONTRACT = {
  version: "actionflow-execution-control.v1",
  controls: [
    "external-cancellation",
    "timeout",
    "in-flight-deduplication",
    "bounded-idempotency",
  ],
  duplicatePolicies: ["parallel", "join", "reject"],
} as const;

export const KG_OBSERVATION_CONTRACT = {
  evidenceTypes: [
    "actionflow-runtime-replay",
    "actionflow-structural-trace",
    "fallback-promotion-readiness",
  ],
  validationResults: ["pass", "fail", "blocked"],
} as const;

export const ACTIONFLOW_STATE_SCOPE_TAXONOMY = {
  stateScopes: [
    "uiState",
    "blockLogicState",
    "applicationGlobalState",
    "unclassified",
  ],
  dataSources: [
    "userInput",
    "localDb",
    "external",
    "appGlobal",
    "env",
    "route",
    "declared",
    "unknown",
  ],
} as const;

export const ACTIONFLOW_PRIMITIVE_TAXONOMY = {
  primitiveKinds: [
    "set-local-state",
    "promote-local-to-global",
    "hydrate-local-from-global",
    "fetch-datasource-to-global",
    "write-global-to-datasource",
    "compute-projection",
    "navigate",
    "guard-by-permission",
    "trigger-ui-feedback",
    "subscribe-realtime-source",
  ],
  effectBoundaryKinds: [
    "pure",
    "state",
    "network",
    "navigation",
    "ui",
    "auth",
    "storage",
    "external",
    "device",
    "media",
    "ai",
    "crypto",
    "realtime",
  ],
  stateRelationKinds: [
    "write",
    "promote",
    "hydrate",
    "cache",
    "sync",
    "derive",
    "read",
    "guard",
  ],
  paramRoles: [
    "source",
    "target",
    "value",
    "condition",
    "payload",
    "route",
    "capability",
    "adapter-option",
    "result",
    "error",
  ],
} as const;

export const ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS = {
  "set-local-state": {
    compilerTargetActions: ["setState"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["state"],
    stateRelationKinds: ["write"],
    paramRoles: ["target", "value"],
  },
  "promote-local-to-global": {
    compilerTargetActions: ["setState"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["state"],
    stateRelationKinds: ["promote"],
    paramRoles: ["source", "target", "value"],
  },
  "hydrate-local-from-global": {
    compilerTargetActions: ["setState"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["state"],
    stateRelationKinds: ["hydrate"],
    paramRoles: ["source", "target", "value"],
  },
  "fetch-datasource-to-global": {
    compilerTargetActions: ["restApi"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["network"],
    stateRelationKinds: ["cache"],
    paramRoles: ["source", "target", "result", "adapter-option"],
  },
  "write-global-to-datasource": {
    compilerTargetActions: ["restApi", "storage", "supabaseQuery"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["network", "storage", "external"],
    stateRelationKinds: ["sync"],
    paramRoles: ["source", "payload", "result", "adapter-option"],
  },
  "compute-projection": {
    compilerTargetActions: ["compute"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["pure", "state"],
    stateRelationKinds: ["derive"],
    paramRoles: ["source", "target", "value"],
  },
  navigate: {
    compilerTargetActions: ["navigate"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["navigation"],
    stateRelationKinds: ["read"],
    paramRoles: ["route", "payload"],
  },
  "guard-by-permission": {
    compilerTargetActions: [],
    compilerTargetStepTypes: ["conditional"],
    effectBoundaryKinds: ["auth"],
    stateRelationKinds: ["guard"],
    paramRoles: ["condition", "capability"],
  },
  "trigger-ui-feedback": {
    compilerTargetActions: ["showToast"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["ui"],
    stateRelationKinds: ["read"],
    paramRoles: ["source", "payload"],
  },
  "subscribe-realtime-source": {
    compilerTargetActions: ["restApi"],
    compilerTargetStepTypes: [],
    effectBoundaryKinds: ["realtime", "network"],
    stateRelationKinds: ["cache"],
    paramRoles: ["source", "target", "result", "adapter-option"],
  },
} as const;

export const EXPRESSION_ONTOLOGY_CONTRACT = {
  canonicalPrefixes: [
    "local",
    "event",
    "props",
    "item",
    "_results",
    "computed",
    "appData",
    "env",
    "route",
  ],
  legacyPrefixes: [
    "state",
    "localState",
    "response",
    "ui",
    "params",
    "page",
  ],
  accessors: accessorProjection.allNames,
} as const;

export const AGENTIC_CONFORMANCE_RELEASE_CONTRACT = {
  version: "agentic-conformance-release.v1",
  requiredRows: [
    "datasourceStateScope",
    "datasourceActionFlowScenarios",
    "rendererFallback",
    "rendererReflection",
    "actionRegistryCoverage",
    "promotionLifecycle",
    "runtimeReplay",
    "structuralTrace",
    "promotionReadiness",
  ],
} as const;

export const ACTIONFLOW_RELEASE_ACTION_COVERAGE = [
  "setState",
  "storage",
  "restApi",
  "callFlow",
  "navigate",
  "showToast",
] as const;

export const ACTIONFLOW_DATASOURCE_SCENARIOS = [
  "userInput",
  "localDb",
  "external",
] as const;

export const FALLBACK_PROMOTION_LIFECYCLE_STATUSES = [
  "quarantined",
  "needs-evidence",
  "ready-for-patch",
] as const;

export const ACTIONFLOW_ONTOLOGY_V1_CONTRACT = {
  version: "actionflow-ontology.v1",
  applicationArchetypes: [
    "cart-checkout",
    "search-filter-table",
    "crud-admin",
    "route-detail-hydration",
    "auth-gated-mutation",
    "offline-storage-sync",
    "multi-step-wizard",
    "file-upload-download",
    "notification-modal-flow",
    "contact-form-submission",
    "realtime-chat-subscription",
    "booking-scheduling",
    "report-export-workflow",
  ],
  outcomeCriterionKinds: [
    "simulation-ok",
    "state-path-equals",
    "state-path-exists",
    "state-path-absent",
    "state-path-length",
    "step-status",
    "resolved-binding-equals",
  ],
  readinessStages: [
    "topology-validation",
    "runtime-simulation",
    "user-outcome",
    "browser-trace",
  ],
  failureModes: [
    "invalid-topology",
    "invalid-expression",
    "missing-state-owner",
    "unsupported-primitive",
    "unsafe-side-effect",
    "missing-datasource-mock",
    "unmet-user-outcome",
    "browser-reflection-mismatch",
    "retrieval-gap",
  ],
  recoveryStrategies: [
    "block-publish",
    "request-regeneration",
    "require-human-review",
    "fallback-to-safe-noop",
    "attach-gap-report",
  ],
  guardPolicies: [
    "fail-closed",
    "skip-side-effects",
    "require-capability",
    "preserve-prior-state",
  ],
} as const;

export const ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS = [
  {
    scenarioId: "cart-checkout",
    archetype: "cart-checkout",
    persona: "shopper",
    userGoal: "Add products to a cart and see a trustworthy payable total.",
    userValue:
      "Local product choice becomes global cart state with computed totals before checkout.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "promote-local-to-global",
      "compute-projection",
    ],
    requiredStateScopes: ["local", "appData", "computed"],
    requiredEffectBoundaryKinds: ["state", "pure"],
    requiredStateRelationKinds: ["write", "promote", "derive"],
    requiredParamRoles: ["source", "target", "value"],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-length",
      "state-path-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "invalid-expression",
      "missing-state-owner",
      "browser-reflection-mismatch",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "search-filter-table",
    archetype: "search-filter-table",
    persona: "operator",
    userGoal: "Filter tabular data and see rows and counts update.",
    userValue:
      "Local filters drive datasource cache and computed result count without ad hoc state copies.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "fetch-datasource-to-global",
      "compute-projection",
    ],
    requiredStateScopes: ["local", "appData", "computed", "datasource", "env"],
    requiredEffectBoundaryKinds: ["state", "network", "pure"],
    requiredStateRelationKinds: ["write", "cache", "derive"],
    requiredParamRoles: [
      "source",
      "target",
      "value",
      "result",
      "adapter-option",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-equals",
      "state-path-length",
      "resolved-binding-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "missing-datasource-mock",
      "invalid-expression",
      "retrieval-gap",
    ],
    recoveryStrategies: ["block-publish", "attach-gap-report"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "crud-admin",
    archetype: "crud-admin",
    persona: "backoffice operator",
    userGoal: "Create or update a protected business record.",
    userValue:
      "Admin mutations preserve state ownership, datasource result evidence, and blocked failure modes.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "write-global-to-datasource",
      "fetch-datasource-to-global",
      "compute-projection",
    ],
    requiredStateScopes: [
      "local",
      "appData",
      "datasource",
      "_results",
      "computed",
    ],
    requiredEffectBoundaryKinds: ["state", "network", "pure"],
    requiredStateRelationKinds: ["write", "sync", "cache", "derive"],
    requiredParamRoles: [
      "source",
      "target",
      "payload",
      "result",
      "adapter-option",
      "value",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-exists",
      "state-path-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "unsafe-side-effect",
      "missing-datasource-mock",
      "browser-reflection-mismatch",
    ],
    recoveryStrategies: ["block-publish", "require-human-review"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "route-detail-hydration",
    archetype: "route-detail-hydration",
    persona: "viewer",
    userGoal: "Open a detail route and see the entity selected by route params.",
    userValue:
      "Route context hydrates datasource results into appData and a local editing draft.",
    requiredPrimitiveKinds: [
      "fetch-datasource-to-global",
      "hydrate-local-from-global",
      "compute-projection",
    ],
    requiredStateScopes: [
      "route",
      "env",
      "datasource",
      "appData",
      "local",
      "_results",
    ],
    requiredEffectBoundaryKinds: ["network", "state", "pure"],
    requiredStateRelationKinds: ["cache", "hydrate", "derive"],
    requiredParamRoles: [
      "source",
      "target",
      "value",
      "result",
      "adapter-option",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-equals",
      "resolved-binding-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "invalid-expression",
      "missing-datasource-mock",
      "browser-reflection-mismatch",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "auth-gated-mutation",
    archetype: "auth-gated-mutation",
    persona: "backoffice operator",
    userGoal: "Attempt a protected mutation and block it without permission.",
    userValue:
      "Unauthorized actions skip side effects and preserve prior datasource state.",
    requiredPrimitiveKinds: [
      "guard-by-permission",
      "write-global-to-datasource",
    ],
    requiredStateScopes: ["appData", "datasource", "_results"],
    requiredEffectBoundaryKinds: ["auth", "network"],
    requiredStateRelationKinds: ["guard", "sync"],
    requiredParamRoles: [
      "condition",
      "capability",
      "source",
      "payload",
      "result",
      "adapter-option",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-absent",
      "step-status",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: ["unsafe-side-effect", "unmet-user-outcome"],
    recoveryStrategies: ["block-publish", "fallback-to-safe-noop"],
    guardPolicies: [
      "fail-closed",
      "skip-side-effects",
      "preserve-prior-state",
    ],
  },
  {
    scenarioId: "offline-storage-sync",
    archetype: "offline-storage-sync",
    persona: "returning user",
    userGoal: "Persist app state to local storage for continuity.",
    userValue:
      "Global state can be written to storage with explicit storage boundary evidence.",
    requiredPrimitiveKinds: [
      "write-global-to-datasource",
      "hydrate-local-from-global",
    ],
    requiredStateScopes: ["appData", "datasource", "_results", "local"],
    requiredEffectBoundaryKinds: ["storage", "state"],
    requiredStateRelationKinds: ["sync", "hydrate"],
    requiredParamRoles: [
      "source",
      "payload",
      "result",
      "adapter-option",
      "target",
      "value",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-exists",
      "state-path-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: ["missing-datasource-mock", "unsafe-side-effect"],
    recoveryStrategies: ["block-publish", "attach-gap-report"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "multi-step-wizard",
    archetype: "multi-step-wizard",
    persona: "new user",
    userGoal: "Complete a multi-step draft and promote it to shared app state.",
    userValue:
      "Block-local draft/step state derives validation and promotes only completed data.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "compute-projection",
      "promote-local-to-global",
    ],
    requiredStateScopes: ["local", "computed", "appData"],
    requiredEffectBoundaryKinds: ["state", "pure"],
    requiredStateRelationKinds: ["write", "derive", "promote"],
    requiredParamRoles: ["source", "target", "value"],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-equals",
      "state-path-exists",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "missing-state-owner",
      "invalid-expression",
      "unmet-user-outcome",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "file-upload-download",
    archetype: "file-upload-download",
    persona: "operator",
    userGoal: "Upload or download a file and see completion evidence.",
    userValue:
      "External file effects are explicit, capability-gated, and surfaced as result state.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "write-global-to-datasource",
      "compute-projection",
    ],
    requiredStateScopes: [
      "local",
      "appData",
      "datasource",
      "_results",
      "computed",
    ],
    requiredEffectBoundaryKinds: ["state", "external", "network", "pure"],
    requiredStateRelationKinds: ["write", "sync", "derive"],
    requiredParamRoles: [
      "source",
      "payload",
      "result",
      "adapter-option",
      "target",
      "value",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-exists",
      "resolved-binding-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: ["unsupported-primitive", "unsafe-side-effect", "retrieval-gap"],
    recoveryStrategies: [
      "block-publish",
      "require-human-review",
      "attach-gap-report",
    ],
    guardPolicies: ["require-capability", "fail-closed"],
  },
  {
    scenarioId: "notification-modal-flow",
    archetype: "notification-modal-flow",
    persona: "user",
    userGoal: "Trigger a notification or modal after a successful interaction.",
    userValue:
      "UI side effects are tied to completed state changes and do not replace state proof.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "compute-projection",
      "trigger-ui-feedback",
    ],
    requiredStateScopes: ["local", "appData", "computed"],
    requiredEffectBoundaryKinds: ["state", "ui", "pure"],
    requiredStateRelationKinds: ["write", "derive", "read"],
    requiredParamRoles: ["target", "value", "source", "payload"],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-equals",
      "state-path-exists",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "browser-reflection-mismatch",
      "unmet-user-outcome",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "contact-form-submission",
    archetype: "contact-form-submission",
    persona: "site visitor",
    userGoal: "Complete a contact form and see a trustworthy submission confirmation.",
    userValue:
      "Contact fields remain explicit draft state, completed input is promoted, and submission feedback is visible.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "promote-local-to-global",
      "trigger-ui-feedback",
    ],
    requiredStateScopes: ["local", "appData"],
    requiredEffectBoundaryKinds: ["state", "ui"],
    requiredStateRelationKinds: ["write", "promote", "read"],
    requiredParamRoles: ["source", "target", "value", "payload"],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-exists",
      "state-path-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "missing-state-owner",
      "unmet-user-outcome",
      "browser-reflection-mismatch",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "realtime-chat-subscription",
    archetype: "realtime-chat-subscription",
    persona: "collaborator",
    userGoal: "Send and receive realtime messages with visible delivery state.",
    userValue:
      "Realtime effects are represented as external boundaries with cached appData and result evidence.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "subscribe-realtime-source",
      "fetch-datasource-to-global",
      "write-global-to-datasource",
      "compute-projection",
    ],
    requiredStateScopes: [
      "local",
      "appData",
      "datasource",
      "_results",
      "computed",
    ],
    requiredEffectBoundaryKinds: ["state", "realtime", "network", "pure"],
    requiredStateRelationKinds: ["write", "cache", "sync", "derive"],
    requiredParamRoles: [
      "source",
      "target",
      "payload",
      "result",
      "adapter-option",
      "value",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-length",
      "state-path-exists",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "unsupported-primitive",
      "missing-datasource-mock",
      "retrieval-gap",
    ],
    recoveryStrategies: ["block-publish", "attach-gap-report"],
    guardPolicies: ["fail-closed"],
  },
  {
    scenarioId: "booking-scheduling",
    archetype: "booking-scheduling",
    persona: "customer",
    userGoal: "Select a time slot and create a booking safely.",
    userValue:
      "Local slot selection is checked, promoted, synced, and reflected with confirmation evidence.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "guard-by-permission",
      "promote-local-to-global",
      "write-global-to-datasource",
      "compute-projection",
    ],
    requiredStateScopes: [
      "local",
      "appData",
      "datasource",
      "_results",
      "computed",
    ],
    requiredEffectBoundaryKinds: ["state", "auth", "network", "pure"],
    requiredStateRelationKinds: ["write", "guard", "promote", "sync", "derive"],
    requiredParamRoles: [
      "source",
      "target",
      "value",
      "condition",
      "capability",
      "payload",
      "result",
      "adapter-option",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-equals",
      "state-path-exists",
      "step-status",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "unsafe-side-effect",
      "unmet-user-outcome",
      "missing-datasource-mock",
    ],
    recoveryStrategies: ["block-publish", "request-regeneration"],
    guardPolicies: ["fail-closed", "require-capability"],
  },
  {
    scenarioId: "report-export-workflow",
    archetype: "report-export-workflow",
    persona: "analyst",
    userGoal: "Generate and export a report from filtered source data.",
    userValue:
      "Report filters, datasource rows, computed summary, and export result are all evidence-backed.",
    requiredPrimitiveKinds: [
      "set-local-state",
      "fetch-datasource-to-global",
      "compute-projection",
      "write-global-to-datasource",
    ],
    requiredStateScopes: [
      "local",
      "appData",
      "computed",
      "datasource",
      "_results",
      "env",
    ],
    requiredEffectBoundaryKinds: ["state", "network", "external", "pure"],
    requiredStateRelationKinds: ["write", "cache", "derive", "sync"],
    requiredParamRoles: [
      "source",
      "target",
      "value",
      "result",
      "adapter-option",
      "payload",
    ],
    requiredOutcomeCriterionKinds: [
      "simulation-ok",
      "state-path-length",
      "state-path-exists",
      "resolved-binding-equals",
    ],
    requiredReadinessStages: [
      "topology-validation",
      "runtime-simulation",
      "user-outcome",
      "browser-trace",
    ],
    failureModes: [
      "missing-datasource-mock",
      "browser-reflection-mismatch",
      "retrieval-gap",
    ],
    recoveryStrategies: ["block-publish", "attach-gap-report"],
    guardPolicies: ["fail-closed"],
  },
] as const;
